// ============================================
// HOOK ACTUALIZADO para usar el servicio correcto
// src/features/dashboard/hooks/useDashboardData.ts
// ============================================

import { useState, useEffect, useCallback } from 'react';
import { useNotify } from 'react-admin';
// Usar el servicio nativo para mejor control de errores
import { dashboardApiService } from '../services/dashboardApi';
import {
  DashboardSummaryResponse,
  UsageTrendsResponse,
  ZonePerformanceResponse,
  AssignmentEfficiencyResponse,
} from '../types';

interface UseDashboardDataOptions {
  hotelId?: string;
  period?: string;
  autoRefresh?: boolean;
  refreshInterval?: number;
}

export const useDashboardData = ({
  hotelId,
  period = 'today',
  autoRefresh = true,
  refreshInterval = 30000,
}: UseDashboardDataOptions) => {
  const notify = useNotify();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [dashboardSummary, setDashboardSummary] = useState<DashboardSummaryResponse | null>(null);
  const [usageTrends, setUsageTrends] = useState<UsageTrendsResponse | null>(null);
  const [zonePerformance, setZonePerformance] = useState<ZonePerformanceResponse | null>(null);
  const [assignmentEfficiency, setAssignmentEfficiency] = useState<AssignmentEfficiencyResponse | null>(null);

  const fetchDashboardData = useCallback(async () => {
    if (!hotelId) {
     
      setLoading(false);
      return;
    }
  
    
    try {
      setLoading(true);
      setError(null);
      
      // Hacer las llamadas en paralelo pero manejar errores individualmente
      const results = await Promise.allSettled([
        dashboardApiService.getDashboardSummary(hotelId, period),
        dashboardApiService.getUsageTrends(hotelId),
        dashboardApiService.getZonePerformance(hotelId),
        dashboardApiService.getAssignmentEfficiency(hotelId, period),
      ]);
      
      // Procesar resultados
      if (results[0].status === 'fulfilled') {
        setDashboardSummary(results[0].value);
      } else {
        console.error('Error fetching dashboard summary:', results[0].reason);
      }
      
      if (results[1].status === 'fulfilled') {
        setUsageTrends(results[1].value);
      } else {
        console.error('Error fetching usage trends:', results[1].reason);
      }
      
      if (results[2].status === 'fulfilled') {
        setZonePerformance(results[2].value);
      } else {
        console.error('Error fetching zone performance:', results[2].reason);
      }
      
      if (results[3].status === 'fulfilled') {
        setAssignmentEfficiency(results[3].value);
      } else {
        console.error('Error fetching assignment efficiency:', results[3].reason);
      }
      
      // Si al menos una llamada fue exitosa, no mostrar error general
      const hasAnySuccess = results.some(r => r.status === 'fulfilled');
      if (!hasAnySuccess) {
        const errorMessage = 'Error loading all dashboard data';
        setError(errorMessage);
        notify(errorMessage, { type: 'error' });
      }
      
    } catch (err: any) {
      const errorMessage = err.message || 'Error loading dashboard data';
      console.error('Dashboard fetch error:', err);
      setError(errorMessage);
      notify(errorMessage, { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [hotelId, period, notify]);

  useEffect(() => {
    fetchDashboardData();
    
    if (autoRefresh && refreshInterval > 0 && hotelId) {
      const interval = setInterval(fetchDashboardData, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [fetchDashboardData, autoRefresh, refreshInterval, hotelId]);

  return {
    loading,
    error,
    dashboardSummary,
    usageTrends,
    zonePerformance,
    assignmentEfficiency,
    refresh: fetchDashboardData,
  };
};